export type AppOperatorExecutionMode =
  | 'workspace_shell'
  | 'browser_bridge'
  | 'server';

export type AppOperatorPresentationMode = 'humanized' | 'instant';

export const APP_OPERATOR_HUMANIZED_TIMING_MS = {
  actionResult: 120,
  click: 120,
  fieldResult: 220,
  scroll: 180,
  targetWithoutGeometry: 140,
  typingKey: 45,
} as const;

export type AppOperatorScrollDirection = 'up' | 'down' | 'left' | 'right';

export interface AppOperatorRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AppOperatorBridgeCommand {
  customAppId: string;
  controlId: string;
  payload: Record<string, unknown>;
}

export interface AppOperatorBridgeTarget {
  controlId: string;
  kind: string;
  label?: string;
  rect?: AppOperatorRect;
  fieldRect?: AppOperatorRect;
  scrollDirection?: AppOperatorScrollDirection;
}

export interface AppOperatorBridgeExecuteResult {
  controlId: string;
  success: true;
  result?: unknown;
  target?: AppOperatorBridgeTarget;
}

export interface AppOperatorToolResultData {
  success: true;
  customAppId: string;
  targetDocId: string;
  controlId: string;
  type: 'route' | 'action' | 'state';
  executionMode: AppOperatorExecutionMode;
  navigateTo?: string;
  bridgeCommand?: {
    controlId: string;
    payload: Record<string, unknown>;
  };
  message?: string;
}

export interface AppOperatorClientActionResult {
  docId: string;
  customAppId: string;
  controlId: string;
  executionMode: AppOperatorExecutionMode;
  navigateTo?: string;
  bridgeResult?: AppOperatorBridgeExecuteResult;
}

export type AppOperatorIframeMessage =
  | {
      type: 'streamlain:app-operator-command/v1';
      requestId: string;
      appId: string;
      operatorBridgeToken: string;
      phase: 'describe' | 'execute';
      command: AppOperatorBridgeCommand;
    }
  | {
      type: 'streamlain:app-operator-response/v1';
      requestId: string;
      appId: string;
      success: true;
      target?: AppOperatorBridgeTarget;
      result?: AppOperatorBridgeExecuteResult;
    }
  | {
      type: 'streamlain:app-operator-response/v1';
      requestId: string;
      appId: string;
      success: false;
      error: string;
    };

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function normalizeAppOperatorRect(
  value: unknown
): AppOperatorRect | undefined {
  if (!isRecord(value)) return undefined;
  const x = value.x;
  const y = value.y;
  const width = value.width ?? value.w;
  const height = value.height ?? value.h;
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return undefined;
  }
  return { x, y, width, height };
}

export function centerOfAppOperatorRect(rect: AppOperatorRect): {
  x: number;
  y: number;
} {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

function bezierAt(
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number
): number {
  const u = 1 - t;
  return (
    u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
  );
}

export function easeAppOperatorMotion(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

export function createNaturalAppOperatorPath(input: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  progress: number;
}): { x: number; y: number } {
  const sx = input.from.x;
  const sy = input.from.y;
  const tx = input.to.x;
  const ty = input.to.y;
  const dx = tx - sx;
  const dy = ty - sy;
  const dist = Math.hypot(dx, dy);
  const perpX = dist > 0 ? -dy / dist : 0;
  const perpY = dist > 0 ? dx / dist : 0;
  const spread = dist * 0.18;
  const c1x = sx + dx * 0.28 + perpX * spread;
  const c1y = sy + dy * 0.28 + perpY * spread;
  const c2x = sx + dx * 0.72 - perpX * spread * 0.7;
  const c2y = sy + dy * 0.72 - perpY * spread * 0.7;
  const t = easeAppOperatorMotion(Math.min(1, Math.max(0, input.progress)));
  return {
    x: bezierAt(sx, c1x, c2x, tx, t),
    y: bezierAt(sy, c1y, c2y, ty, t),
  };
}

export function appOperatorMotionDurationMs(input: {
  from: { x: number; y: number };
  to: { x: number; y: number };
}): number {
  const distance = Math.hypot(
    input.to.x - input.from.x,
    input.to.y - input.from.y
  );
  return Math.max(240, Math.min(1200, distance * 1.35));
}
